import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, formatDate, formatDateTime } from '../../utils/api'

const STATUS_FILTERS = [
  { value: 'active', label: 'All Active' },
  { value: 'logged', label: 'Open' },
  { value: 'awaiting_evidence', label: 'Awaiting Evidence' },
  { value: 'evidence_received', label: 'Evidence Received' },
  { value: 'troubleshooting_sent', label: 'Troubleshooting' },
  { value: 'return_approved', label: 'Approved' },
  { value: 'return_booked', label: 'Awaiting Label' },
  { value: 'closed', label: 'Closed' }
]

const STATUS_STYLES = {
  logged: { bg: '#dbeafe', color: '#1e40af', label: 'Open' },
  awaiting_evidence: { bg: '#fef3c7', color: '#92400e', label: 'Awaiting Evidence' },
  troubleshooting_sent: { bg: '#e0e7ff', color: '#3730a3', label: 'Troubleshooting' },
  return_approved: { bg: '#d1fae5', color: '#065f46', label: 'Approved' },
  return_booked: { bg: '#fef3c7', color: '#92400e', label: 'Awaiting Label' },
  return_label_sent: { bg: '#dbeafe', color: '#1e40af', label: 'Return Booked' },
  refunded: { bg: '#d1fae5', color: '#065f46', label: 'Refunded' },
  rejected: { bg: '#fef2f2', color: '#991b1b', label: 'Rejected' },
  closed: { bg: '#f3f4f6', color: '#6b7280', label: 'Closed' }
}

function StatusBadge({ status, evidenceReceived }) {
  const s = STATUS_STYLES[status] || { bg: '#f3f4f6', color: '#6b7280', label: status }
  const showEvidenceReceived = status === 'awaiting_evidence' && evidenceReceived
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: showEvidenceReceived ? '#d1fae5' : s.bg, color: showEvidenceReceived ? '#065f46' : s.color }}>
      {showEvidenceReceived ? 'Evidence Received' : s.label}
    </span>
  )
}

function CasesTab() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialCaseId = searchParams.get('caseId') || null
  const [cases, setCases] = useState([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('active')
  const [selectedCase, setSelectedCase] = useState(null)
  const [caseEvidence, setCaseEvidence] = useState([])
  const [caseLoading, setCaseLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [troubleshootingLoading, setTroubleshootingLoading] = useState(false)
  const [overrulingTroubleshooting, setOverrulingTroubleshooting] = useState(false)
  const [proposedMessage, setProposedMessage] = useState(null)
  const [sendingEmail, setSendingEmail] = useState(false)
  const [evidenceType, setEvidenceType] = useState({ photos: true, videos: false })
  const [troubleshootingSteps, setTroubleshootingSteps] = useState([])
  const [generatingRequest, setGeneratingRequest] = useState(false)
  const [evidenceDraft, setEvidenceDraft] = useState(null)
  const [evidenceDraftMessage, setEvidenceDraftMessage] = useState('')
  const [sendingEvidenceRequest, setSendingEvidenceRequest] = useState(false)
  const [evidenceReviewLoading, setEvidenceReviewLoading] = useState(false)
  const [rejectingReturn, setRejectingReturn] = useState(false)
  const [caseNotes, setCaseNotes] = useState([])
  const [caseHistory, setCaseHistory] = useState([])
  const [newNote, setNewNote] = useState('')
  const [addingNote, setAddingNote] = useState(false)
  const [queryingEvidence, setQueryingEvidence] = useState(false)
  const [queryDraftOpen, setQueryDraftOpen] = useState(false)
  const [queryDraftMessage, setQueryDraftMessage] = useState('')
  const [sendingReturnInstructions, setSendingReturnInstructions] = useState(false)
  const [doNotReturnLoading, setDoNotReturnLoading] = useState(false)
  const [cancellingOrder, setCancellingOrder] = useState(false)
  const [showPartialRefundModal, setShowPartialRefundModal] = useState(false)
  const [partialRefundAmount, setPartialRefundAmount] = useState('')
  const [partialRefundData, setPartialRefundData] = useState(null)
  const [partialRefundLoading, setPartialRefundLoading] = useState(false)
  const [fetchingRefundData, setFetchingRefundData] = useState(false)

  const loadCases = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get(`/api/orders/returns/all?status=${statusFilter}`)
      setCases(res.cases || [])
    } catch (err) {
      console.error('Failed to load cases:', err)
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  useEffect(() => { loadCases() }, [loadCases])

  // Auto-open a case if initialCaseId is provided
  useEffect(() => {
    if (initialCaseId && cases.length > 0) {
      const c = cases.find(c => c.id === parseInt(initialCaseId))
      if (c) selectCase(c)
    }
  }, [initialCaseId, cases])

  async function selectCase(c) {
    setSelectedCase(c)
    setCaseLoading(true)
    setCaseNotes([])
    setCaseHistory([])
    setNewNote('')
    try {
      const [evidenceRes, detailRes, notesRes, historyRes] = await Promise.all([
        api.get(`/api/orders/returns/${c.id}/evidence`),
        api.get(`/api/orders/returns/by-ticket/${c.ticket_id}`),
        api.get(`/api/orders/returns/${c.id}/notes`),
        api.get(`/api/orders/returns/${c.id}/history`)
      ])
      setCaseEvidence(evidenceRes.evidence || [])
      if (detailRes.returnRequest) setSelectedCase(detailRes.returnRequest)
      if (detailRes.troubleshootingSteps) setTroubleshootingSteps(detailRes.troubleshootingSteps)
      setCaseNotes(notesRes.notes || [])
      setCaseHistory(historyRes.history || [])
    } catch (err) {
      console.error('Failed to load case details:', err)
    } finally {
      setCaseLoading(false)
    }
  }

  async function handleEvidenceUpload(e) {
    const files = Array.from(e.target.files)
    if (!files.length || !selectedCase) return
    setUploading(true)
    try {
      for (const file of files) {
        const formData = new FormData()
        formData.append('file', file)
        await fetch(`/api/orders/returns/${selectedCase.id}/evidence`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        })
      }
      // Reload evidence
      const res = await api.get(`/api/orders/returns/${selectedCase.id}/evidence`)
      setCaseEvidence(res.evidence || [])
    } catch (err) {
      alert(`Upload failed: ${err.message}`)
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  async function deleteEvidence(evidenceId) {
    if (!selectedCase) return
    try {
      await api.delete(`/api/orders/returns/${selectedCase.id}/evidence/${evidenceId}`)
      setCaseEvidence(prev => prev.filter(e => e.id !== evidenceId))
    } catch (err) {
      alert(`Delete failed: ${err.message}`)
    }
  }

  async function addNote() {
    if (!selectedCase || !newNote.trim()) return
    setAddingNote(true)
    try {
      const res = await api.post(`/api/orders/returns/${selectedCase.id}/notes`, { content: newNote.trim() })
      if (res.note) setCaseNotes(prev => [res.note, ...prev])
      setNewNote('')
    } catch (err) {
      alert(`Failed to add note: ${err.message}`)
    } finally {
      setAddingNote(false)
    }
  }

  async function deleteNote(noteId) {
    if (!selectedCase) return
    try {
      await api.delete(`/api/orders/returns/${selectedCase.id}/notes/${noteId}`)
      setCaseNotes(prev => prev.filter(n => n.id !== noteId))
    } catch (err) {
      alert(`Failed to delete note: ${err.message}`)
    }
  }

  async function requestEvidence() {
    if (!selectedCase) return
    setGeneratingRequest(true)
    try {
      const types = []
      if (evidenceType.photos) types.push('photos')
      if (evidenceType.videos) types.push('videos')
      // Get AI-composed evidence request message as draft for review
      const res = await api.post(`/api/orders/returns/${selectedCase.id}/compose-evidence-request`, { types, draftOnly: true })
      if (res.success && res.message) {
        setEvidenceDraft({ types })
        setEvidenceDraftMessage(res.message)
      }
    } catch (err) {
      alert(`Failed to generate evidence request: ${err.message}`)
    } finally {
      setGeneratingRequest(false)
    }
  }

  async function sendEvidenceRequest() {
    if (!selectedCase || !evidenceDraftMessage.trim()) return
    setSendingEvidenceRequest(true)
    try {
      const types = evidenceDraft?.types || ['photos', 'videos']
      await api.post(`/api/orders/returns/${selectedCase.id}/compose-evidence-request`, {
        types,
        message: evidenceDraftMessage.trim()
      })
      setEvidenceDraft(null)
      setEvidenceDraftMessage('')
      const detailRes = await api.get(`/api/orders/returns/by-ticket/${selectedCase.ticket_id}`)
      if (detailRes.returnRequest) setSelectedCase(detailRes.returnRequest)
      loadCases()
    } catch (err) {
      alert(`Failed to send evidence request: ${err.message}`)
    } finally {
      setSendingEvidenceRequest(false)
    }
  }

  async function generateTroubleshooting() {
    if (!selectedCase) return
    setTroubleshootingLoading(true)
    try {
      const res = await api.post(`/api/orders/returns/${selectedCase.id}/troubleshoot`)
      setProposedMessage(res.troubleshooting)
      // Reload case to get updated content and steps
      const detailRes = await api.get(`/api/orders/returns/by-ticket/${selectedCase.ticket_id}`)
      if (detailRes.returnRequest) setSelectedCase(detailRes.returnRequest)
      if (detailRes.troubleshootingSteps) setTroubleshootingSteps(detailRes.troubleshootingSteps)
    } catch (err) {
      alert(`Failed to generate troubleshooting: ${err.message}`)
    } finally {
      setTroubleshootingLoading(false)
    }
  }

  async function overruleTroubleshooting() {
    if (!selectedCase) return
    if (!confirm('Are you sure you want to bypass troubleshooting and approve this return?')) return
    setOverrulingTroubleshooting(true)
    try {
      await api.post(`/api/orders/returns/${selectedCase.id}/overrule-troubleshooting`)
      const detailRes = await api.get(`/api/orders/returns/by-ticket/${selectedCase.ticket_id}`)
      if (detailRes.returnRequest) setSelectedCase(detailRes.returnRequest)
    } catch (err) {
      alert(`Failed to overrule troubleshooting: ${err.message}`)
    } finally {
      setOverrulingTroubleshooting(false)
    }
  }

  // Extract key frames from a video by loading through our API proxy (avoids R2 CORS issues)
  async function extractVideoFrames(evidenceId, numFrames = 4) {
    const apiBase = import.meta.env.VITE_API_URL || ''
    // Fetch video as blob through our worker proxy (uses cookie auth)
    const response = await fetch(`${apiBase}/api/orders/returns/evidence/${evidenceId}/file`, {
      credentials: 'include'
    })
    if (!response.ok) throw new Error('Failed to fetch video')
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)

    try {
      return await new Promise((resolve, reject) => {
        const video = document.createElement('video')
        video.muted = true
        video.preload = 'auto'

        const timeout = setTimeout(() => {
          video.src = ''
          reject(new Error('Video frame extraction timed out'))
        }, 30000)

        video.onloadedmetadata = () => {
          const duration = video.duration
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          canvas.width = Math.min(video.videoWidth, 1280)
          canvas.height = Math.round(canvas.width * (video.videoHeight / video.videoWidth))

          const frames = []
          const times = []
          for (let i = 0; i < numFrames; i++) {
            times.push(numFrames === 1 ? duration * 0.25 : (duration * i) / (numFrames - 1))
          }

          let currentFrame = 0

          video.onseeked = () => {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
            const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
            frames.push(dataUrl.split(',')[1])
            currentFrame++

            if (currentFrame < times.length) {
              video.currentTime = times[currentFrame]
            } else {
              clearTimeout(timeout)
              video.src = ''
              resolve(frames)
            }
          }

          video.currentTime = times[0]
        }

        video.onerror = () => {
          clearTimeout(timeout)
          reject(new Error('Failed to load video for frame extraction'))
        }
        video.src = blobUrl
      })
    } finally {
      URL.revokeObjectURL(blobUrl)
    }
  }

  async function reviewEvidence() {
    if (!selectedCase) return
    setEvidenceReviewLoading(true)
    try {
      // Extract frames from any videos before sending to backend
      let videoFrames = []
      const vids = caseEvidence.filter(e => e.file_type === 'video')
      for (const vid of vids.slice(0, 2)) {
        try {
          const frames = await extractVideoFrames(vid.id, 4)
          videoFrames.push(...frames)
        } catch (frameErr) {
          console.warn('Could not extract frames from video:', vid.original_filename, frameErr)
        }
      }

      await api.post(`/api/orders/returns/${selectedCase.id}/review-evidence`, {
        video_frames: videoFrames.slice(0, 8)
      })
      const detailRes = await api.get(`/api/orders/returns/by-ticket/${selectedCase.ticket_id}`)
      if (detailRes.returnRequest) setSelectedCase(detailRes.returnRequest)
    } catch (err) {
      alert(`Failed to review evidence: ${err.message}`)
    } finally {
      setEvidenceReviewLoading(false)
    }
  }

  async function rejectReturn() {
    if (!selectedCase) return
    if (!window.confirm('Reject this return and notify the customer? The rejection email will reference the AI evidence review findings.')) return
    setRejectingReturn(true)
    try {
      await api.post(`/api/orders/returns/${selectedCase.id}/reject`)
      const detailRes = await api.get(`/api/orders/returns/by-ticket/${selectedCase.ticket_id}`)
      if (detailRes.returnRequest) setSelectedCase(detailRes.returnRequest)
      loadCases()
    } catch (err) {
      alert(`Failed to reject return: ${err.message}`)
    } finally {
      setRejectingReturn(false)
    }
  }

  function openQueryDraft() {
    if (!selectedCase) return
    // Pre-populate with AI-generated summary if available
    let defaultMessage = ''
    try {
      const review = JSON.parse(selectedCase.evidence_review || '{}')
      if (review.red_flags && review.red_flags.length > 0) {
        const issues = review.red_flags.map(f => f.detail).join('. ')
        defaultMessage = `Thank you for submitting evidence for your return request regarding your ${selectedCase.product_title || 'item'}.\n\nWe have reviewed the evidence provided, however we have noticed a discrepancy. Specifically, ${issues}.\n\nCould you please provide further information or additional evidence to help us understand the issue?`
      }
    } catch {}
    if (!defaultMessage) {
      defaultMessage = `We have reviewed the evidence provided for your ${selectedCase.product_title || 'item'} case, however we require additional information.\n\n`
    }
    setQueryDraftMessage(defaultMessage)
    setQueryDraftOpen(true)
  }

  async function queryEvidence() {
    if (!selectedCase || !queryDraftMessage.trim()) return
    setQueryingEvidence(true)
    try {
      await api.post(`/api/orders/returns/${selectedCase.id}/query-evidence`, {
        message: queryDraftMessage.trim()
      })
      setQueryDraftOpen(false)
      setQueryDraftMessage('')
      const detailRes = await api.get(`/api/orders/returns/by-ticket/${selectedCase.ticket_id}`)
      if (detailRes.returnRequest) setSelectedCase(detailRes.returnRequest)
    } catch (err) {
      alert(`Failed to send evidence query: ${err.message}`)
    } finally {
      setQueryingEvidence(false)
    }
  }

  async function sendTroubleshooting() {
    if (!selectedCase || !proposedMessage) return
    setSendingEmail(true)
    try {
      // Send via dedicated backend endpoint (no-reply, Audio Trader Technical Support Team)
      await api.post(`/api/orders/returns/${selectedCase.id}/send-troubleshooting`, {
        message: proposedMessage
      })
      setProposedMessage(null)
      const detailRes = await api.get(`/api/orders/returns/by-ticket/${selectedCase.ticket_id}`)
      if (detailRes.returnRequest) setSelectedCase(detailRes.returnRequest)
      loadCases()
    } catch (err) {
      alert(`Failed to send troubleshooting: ${err.message}`)
    } finally {
      setSendingEmail(false)
    }
  }

  async function updateCaseStatus(status, extraFields = {}) {
    if (!selectedCase) return
    setCaseLoading(true)
    try {
      await api.patch(`/api/orders/returns/${selectedCase.id}`, { status, ...extraFields })
      const detailRes = await api.get(`/api/orders/returns/by-ticket/${selectedCase.ticket_id}`)
      if (detailRes.returnRequest) setSelectedCase(detailRes.returnRequest)
      loadCases()
    } catch (err) {
      alert(`Failed to update case: ${err.message}`)
    } finally {
      setCaseLoading(false)
    }
  }

  async function openPartialRefundModal() {
    if (!selectedCase) return
    setFetchingRefundData(true)
    try {
      const res = await api.get(`/api/orders/returns/${selectedCase.id}/refund-info`)
      setPartialRefundData(res)
      setPartialRefundAmount('')
      setShowPartialRefundModal(true)
    } catch (err) {
      alert(`Failed to load refund info: ${err.message}`)
    } finally {
      setFetchingRefundData(false)
    }
  }

  async function submitPartialRefund() {
    if (!selectedCase || !partialRefundAmount) return
    const amount = parseFloat(partialRefundAmount)
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount greater than £0')
      return
    }
    if (partialRefundData && amount > partialRefundData.remaining_refundable) {
      alert(`Amount exceeds refundable balance of £${partialRefundData.remaining_refundable.toFixed(2)}`)
      return
    }
    if (!confirm(`Issue a partial refund of £${amount.toFixed(2)} and close this case?`)) return
    setPartialRefundLoading(true)
    try {
      const res = await api.post(`/api/orders/returns/${selectedCase.id}/partial-refund`, { amount })
      alert(res.message || `Partial refund of £${amount.toFixed(2)} issued. Case closed.`)
      setShowPartialRefundModal(false)
      setPartialRefundData(null)
      loadCases()
      const detailRes = await api.get(`/api/orders/returns/by-ticket/${selectedCase.ticket_id}`)
      if (detailRes.returnRequest) setSelectedCase(detailRes.returnRequest)
    } catch (err) {
      alert(`Failed: ${err.message}`)
    } finally {
      setPartialRefundLoading(false)
    }
  }

  const photos = caseEvidence.filter(e => e.file_type === 'photo')
  const videos = caseEvidence.filter(e => e.file_type === 'video')

  // Case detail view
  if (selectedCase) {
    return (
      <div>
        <button
          className="btn btn-sm btn-secondary"
          style={{ marginBottom: 16, fontSize: 12 }}
          onClick={() => { setSelectedCase(null); setProposedMessage(null); setCaseEvidence([]); setTroubleshootingSteps([]) }}
        >
          &larr; Back to Cases
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Left column - Case info */}
          <div>
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h3 style={{ margin: 0, fontSize: 16 }}>Case {selectedCase.public_id || '#' + selectedCase.id}</h3>
                  {selectedCase.channel && selectedCase.channel !== 'shopify' && (
                    <span style={{
                      padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                      background: selectedCase.channel === 'ebay' ? '#e4f0fb' : '#f3e8ff',
                      color: selectedCase.channel === 'ebay' ? '#0654ba' : '#6b21a8'
                    }}>
                      {selectedCase.channel}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <StatusBadge status={selectedCase.status} evidenceReceived={!!selectedCase.evidence_received_at} />
                  {selectedCase.status !== 'closed' && (
                    <button
                      style={{ padding: '2px 8px', fontSize: 11, fontWeight: 600, background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                      onClick={() => {
                        if (!confirm('Close this case?')) return
                        updateCaseStatus('closed', { closed_at: new Date().toISOString(), closed_by: 'staff' })
                      }}
                      disabled={caseLoading}
                    >
                      Close Case
                    </button>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                <div><strong>Issue:</strong> {selectedCase.reason_name}</div>
                {selectedCase.reason_notes && <div><strong>Description:</strong> {selectedCase.reason_notes}</div>}
                {selectedCase.product_title && <div><strong>Product:</strong> {selectedCase.product_title}</div>}
                {selectedCase.order_name && <div><strong>Order:</strong> {selectedCase.order_name}</div>}
                {selectedCase.customer_email && <div><strong>Customer:</strong> {selectedCase.customer_name || selectedCase.customer_email}</div>}
                <div><strong>Opened:</strong> {formatDateTime(selectedCase.created_at)}</div>
                {selectedCase.ticket_subject && (
                  <div>
                    <strong>Ticket:</strong>{' '}
                    <a
                      href="#"
                      onClick={e => { e.preventDefault(); navigate(`/tickets?ticketId=${selectedCase.ticket_id}`) }}
                      style={{ color: '#2563eb' }}
                    >
                      {selectedCase.ticket_subject}
                    </a>
                  </div>
                )}
              </div>

              {/* Marketplace messaging notice */}
              {selectedCase.channel && selectedCase.channel !== 'shopify' && (
                <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, fontSize: 12, lineHeight: 1.5,
                  background: selectedCase.channel === 'ebay' ? '#e4f0fb' : '#f3e8ff',
                  color: selectedCase.channel === 'ebay' ? '#0654ba' : '#6b21a8',
                  border: `1px solid ${selectedCase.channel === 'ebay' ? '#bfdbfe' : '#e9d5ff'}`
                }}>
                  Messages sent via {selectedCase.channel === 'ebay' ? 'eBay' : 'Reverb'} messaging + Gmail backup.
                  {selectedCase.channel === 'ebay' && !selectedCase.ebay_buyer_username && (
                    <span style={{ color: '#dc2626', fontWeight: 600 }}> eBay buyer info not available — Gmail only.</span>
                  )}
                </div>
              )}

              {/* Actions based on status */}
              {(() => {
                const isCancellation = selectedCase.reason_name === 'Pre-Shipment Cancellation'
                return (
              <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {/* --- CANCELLATION FLOW --- */}
                {isCancellation && selectedCase.status === 'logged' && (
                  <div style={{ width: '100%', padding: 12, background: '#fef3c7', borderRadius: 6 }}>
                    <div style={{ fontWeight: 600, color: '#92400e', fontSize: 13, textAlign: 'center', marginBottom: 8 }}>Pre-Shipment Cancellation Request</div>
                    <div style={{ fontSize: 12, color: '#92400e', textAlign: 'center', marginBottom: 10 }}>
                      Customer wants to cancel before dispatch. Approve to proceed with cancellation and refund.
                    </div>
                    <button
                      className="btn btn-sm"
                      style={{ width: '100%', background: '#f59e0b', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12, padding: '8px 12px' }}
                      onClick={() => {
                        if (window.confirm('Approve cancellation? The order will need to be cancelled and refunded.')) {
                          updateCaseStatus('return_approved', { approved_at: new Date().toISOString() })
                        }
                      }}
                      disabled={caseLoading}
                    >
                      Approve Cancellation
                    </button>
                  </div>
                )}
                {isCancellation && selectedCase.status === 'return_approved' && (
                  <div style={{ width: '100%', padding: 12, background: '#fef3c7', borderRadius: 6 }}>
                    <div style={{ fontWeight: 600, color: '#92400e', fontSize: 13, textAlign: 'center', marginBottom: 8 }}>Cancellation Approved</div>
                    {selectedCase.order_name && (
                      <div style={{ fontSize: 12, color: '#92400e', textAlign: 'center', marginBottom: 10 }}>
                        Order: <strong>{selectedCase.order_name}</strong>
                      </div>
                    )}
                    {selectedCase.shopify_order_id ? (
                      <button
                        className="btn btn-sm"
                        style={{ width: '100%', background: '#dc2626', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12, padding: '8px 12px' }}
                        onClick={async () => {
                          if (!window.confirm(`Cancel order ${selectedCase.order_name || ''} on Shopify and issue a full refund? Customer will be emailed confirmation.`)) return
                          setCancellingOrder(true)
                          try {
                            const res = await api.post(`/api/orders/returns/${selectedCase.id}/cancel-and-refund`)
                            alert(res.message || 'Order cancelled, refunded, and customer notified.')
                            const detailRes = await api.get(`/api/orders/returns/by-ticket/${selectedCase.ticket_id}`)
                            if (detailRes.returnRequest) setSelectedCase(detailRes.returnRequest)
                            loadCases()
                          } catch (err) {
                            alert(`Failed: ${err.message}`)
                          } finally {
                            setCancellingOrder(false)
                          }
                        }}
                        disabled={cancellingOrder}
                      >
                        {cancellingOrder ? 'Processing...' : 'Cancel Order & Full Refund'}
                      </button>
                    ) : (
                      <div style={{ fontSize: 12, color: '#92400e', textAlign: 'center' }}>
                        No Shopify order linked. Cancel and refund manually, then close the case.
                      </div>
                    )}
                  </div>
                )}
                {/* --- RETURN FLOW --- */}
                {!isCancellation && selectedCase.status === 'logged' && (
                  <>
                    <div style={{ width: '100%', marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Request evidence from customer:</div>
                      <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                          <input type="checkbox" checked={evidenceType.photos} onChange={e => setEvidenceType(prev => ({ ...prev, photos: e.target.checked }))} />
                          Photos
                        </label>
                        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                          <input type="checkbox" checked={evidenceType.videos} onChange={e => setEvidenceType(prev => ({ ...prev, videos: e.target.checked }))} />
                          Videos
                        </label>
                      </div>
                    </div>
                    <button
                      className="btn btn-sm"
                      style={{ background: '#f59e0b', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12 }}
                      onClick={requestEvidence}
                      disabled={generatingRequest || sendingEvidenceRequest || evidenceDraft || (!evidenceType.photos && !evidenceType.videos)}
                    >
                      {generatingRequest ? 'Generating...' : 'Ask for Evidence'}
                    </button>
                    {evidenceDraft && (
                      <div style={{ width: '100%', marginTop: 10, padding: 10, background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 6 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#92400e', marginBottom: 6 }}>Review email before sending:</div>
                        <textarea
                          value={evidenceDraftMessage}
                          onChange={e => setEvidenceDraftMessage(e.target.value)}
                          style={{ width: '100%', minHeight: 200, padding: 8, fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
                        />
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button
                            className="btn btn-sm"
                            style={{ background: '#f59e0b', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12 }}
                            onClick={sendEvidenceRequest}
                            disabled={sendingEvidenceRequest || !evidenceDraftMessage.trim()}
                          >
                            {sendingEvidenceRequest ? 'Sending...' : 'Send to Customer'}
                          </button>
                          <button
                            className="btn btn-sm"
                            style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', fontWeight: 500, fontSize: 12 }}
                            onClick={() => { setEvidenceDraft(null); setEvidenceDraftMessage('') }}
                            disabled={sendingEvidenceRequest}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {!isCancellation && ['logged', 'awaiting_evidence'].includes(selectedCase.status) && (
                  <>
                    {(photos.length > 0 || videos.length > 0) && (
                      <button
                        className="btn btn-sm"
                        style={{ background: '#0ea5e9', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12 }}
                        onClick={reviewEvidence}
                        disabled={evidenceReviewLoading}
                      >
                        {evidenceReviewLoading ? 'AI Reviewing...' : selectedCase.evidence_review ? 'Re-run AI Review' : 'AI Review Evidence'}
                      </button>
                    )}
                    <button
                      className="btn btn-sm"
                      style={{ background: '#7c3aed', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12, opacity: (selectedCase.has_photos || selectedCase.has_videos) ? 1 : 0.5 }}
                      onClick={generateTroubleshooting}
                      disabled={troubleshootingLoading || (!selectedCase.has_photos && !selectedCase.has_videos)}
                      title={(!selectedCase.has_photos && !selectedCase.has_videos) ? 'Evidence must be uploaded before generating troubleshooting' : ''}
                    >
                      {troubleshootingLoading ? 'AI Generating...' : 'Generate Troubleshooting'}
                    </button>
                    {selectedCase.status === 'logged' && (
                      <button
                        className="btn btn-sm"
                        style={{ background: '#6b7280', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12 }}
                        onClick={() => {
                          if (window.confirm('Skip evidence gathering and approve return directly?')) {
                            updateCaseStatus('return_approved', { approved_at: new Date().toISOString() })
                          }
                        }}
                        disabled={caseLoading}
                      >
                        Evidence Not Required
                      </button>
                    )}
                    <button
                      className="btn btn-sm"
                      style={{ background: '#6b7280', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12 }}
                      onClick={() => {
                        if (window.confirm('Skip troubleshooting and approve return directly?')) {
                          updateCaseStatus('return_approved', { approved_at: new Date().toISOString() })
                        }
                      }}
                      disabled={caseLoading}
                    >
                      Troubleshooting Not Required
                    </button>
                  </>
                )}
                {!isCancellation && selectedCase.status === 'troubleshooting_sent' && (
                  <>
                    <button
                      className="btn btn-sm"
                      style={{ background: '#059669', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12 }}
                      onClick={() => updateCaseStatus('closed', { troubleshooting_resolved: 1, closed_at: new Date().toISOString() })}
                      disabled={caseLoading}
                    >
                      Issue Resolved
                    </button>
                    <button
                      className="btn btn-sm"
                      style={{ background: '#dc2626', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12 }}
                      onClick={() => updateCaseStatus('return_approved', { troubleshooting_resolved: 0, approved_at: new Date().toISOString() })}
                      disabled={caseLoading}
                    >
                      Approve Return
                    </button>
                  </>
                )}
                {!isCancellation && selectedCase.status === 'return_approved' && selectedCase.refund_type === 'no_return' && (
                  <div style={{ width: '100%', padding: 12, background: '#dbeafe', borderRadius: 6 }}>
                    <div style={{ fontWeight: 600, color: '#1e40af', fontSize: 13, textAlign: 'center', marginBottom: 8 }}>No Return Required</div>
                    <div style={{ fontSize: 12, color: '#1e40af', textAlign: 'center', marginBottom: 10 }}>
                      Customer has been notified they do not need to return the item. Process the refund below.
                    </div>
                    <button
                      className="btn btn-sm"
                      style={{ width: '100%', background: '#2563eb', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12, padding: '8px 12px' }}
                      onClick={async () => {
                        if (!confirm('Issue a full Shopify refund for this order? The customer will be notified by Shopify.')) return
                        setDoNotReturnLoading(true)
                        try {
                          const res = await api.post(`/api/orders/returns/${selectedCase.id}/process-refund`)
                          alert(res.message || 'Refund issued and case closed.')
                          loadCases()
                          const detailRes = await api.get(`/api/orders/returns/by-ticket/${selectedCase.ticket_id}`)
                          if (detailRes.returnRequest) setSelectedCase(detailRes.returnRequest)
                        } catch (err) {
                          alert(`Failed: ${err.message}`)
                        } finally {
                          setDoNotReturnLoading(false)
                        }
                      }}
                      disabled={doNotReturnLoading}
                    >
                      {doNotReturnLoading ? 'Processing...' : 'Process Full Refund'}
                    </button>
                  </div>
                )}
                {!isCancellation && selectedCase.status === 'return_approved' && selectedCase.refund_type !== 'no_return' && (
                  <div style={{ width: '100%', padding: 12, background: '#d1fae5', borderRadius: 6 }}>
                    <div style={{ fontWeight: 600, color: '#065f46', fontSize: 13, textAlign: 'center', marginBottom: 8 }}>Return Approved</div>
                    <div style={{ fontSize: 12, color: '#065f46', textAlign: 'center', marginBottom: 10 }}>
                      Send the customer their return portal link to collect package details.
                    </div>
                    <button
                      className="btn btn-sm"
                      style={{ width: '100%', background: '#059669', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12, padding: '8px 12px' }}
                      onClick={async () => {
                        setSendingReturnInstructions(true)
                        try {
                          const res = await api.post(`/api/orders/returns/${selectedCase.id}/send-return-instructions`)
                          alert(res.message || 'Return instructions sent.')
                        } catch (err) {
                          alert(`Failed: ${err.message}`)
                        } finally {
                          setSendingReturnInstructions(false)
                        }
                      }}
                      disabled={sendingReturnInstructions}
                    >
                      {sendingReturnInstructions ? 'Sending...' : 'Send Return Instructions'}
                    </button>
                    <button
                      className="btn btn-sm"
                      style={{ width: '100%', background: '#6b7280', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12, padding: '8px 12px', marginTop: 6 }}
                      onClick={async () => {
                        if (!confirm('This will email the customer that no return is needed and their refund is on the way. Continue?')) return
                        setDoNotReturnLoading(true)
                        try {
                          const res = await api.post(`/api/orders/returns/${selectedCase.id}/do-not-require-return`)
                          alert(res.message || 'Customer notified. Ready for refund.')
                          loadCases()
                          const detailRes = await api.get(`/api/orders/returns/by-ticket/${selectedCase.ticket_id}`)
                          if (detailRes.returnRequest) setSelectedCase(detailRes.returnRequest)
                        } catch (err) {
                          alert(`Failed: ${err.message}`)
                        } finally {
                          setDoNotReturnLoading(false)
                        }
                      }}
                      disabled={doNotReturnLoading || sendingReturnInstructions}
                    >
                      {doNotReturnLoading ? 'Sending...' : 'Do Not Require Return'}
                    </button>
                    {selectedCase.customer_token && (
                      <div style={{ marginTop: 8, fontSize: 11, color: '#065f46', textAlign: 'center' }}>
                        Portal: <a
                          href={`/case/${selectedCase.customer_token}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#047857', textDecoration: 'underline' }}
                        >
                          Open customer portal
                        </a>
                      </div>
                    )}
                  </div>
                )}
                {selectedCase.status === 'rejected' && (
                  <div style={{ width: '100%', padding: 12, background: '#fef2f2', borderRadius: 6, textAlign: 'center' }}>
                    <div style={{ fontWeight: 600, color: '#991b1b', fontSize: 13 }}>Return Rejected</div>
                    <div style={{ fontSize: 12, color: '#991b1b' }}>Evidence did not support the reported issue</div>
                  </div>
                )}
                {selectedCase.status === 'closed' && (
                  <div style={{ width: '100%', padding: 12, background: '#f3f4f6', borderRadius: 6, textAlign: 'center' }}>
                    <div style={{ fontWeight: 600, color: '#6b7280', fontSize: 13 }}>
                      {selectedCase.troubleshooting_resolved ? 'Resolved via Troubleshooting' : selectedCase.refund_type === 'no_return' ? 'Closed — No Return Required' : 'Case Closed'}
                    </div>
                    {selectedCase.closed_by && (
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                        Closed by {selectedCase.closed_by === 'customer' ? 'customer (via portal)' : 'staff'}
                      </div>
                    )}
                  </div>
                )}
                {selectedCase.status !== 'closed' && selectedCase.shopify_order_id && (
                  <button
                    className="btn btn-sm"
                    style={{ width: '100%', marginTop: 8, background: '#f59e0b', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12, padding: '8px 12px' }}
                    onClick={openPartialRefundModal}
                    disabled={fetchingRefundData}
                  >
                    {fetchingRefundData ? 'Loading...' : 'Resolve with Partial Refund'}
                  </button>
                )}
              </div>
                )
              })()}
            </div>

            {/* AI Evidence Review findings */}
            {selectedCase.evidence_review && (() => {
              let review
              try { review = JSON.parse(selectedCase.evidence_review) } catch { return null }
              if (!review) return null
              const verdictStyles = {
                red:   { bg: '#fef2f2', border: '#fca5a5', color: '#991b1b', icon: '\u26d4', label: 'Issues Found' },
                amber: { bg: '#fffbeb', border: '#fcd34d', color: '#92400e', icon: '\u26a0\ufe0f', label: 'Needs Attention' },
                green: { bg: '#f0fdf4', border: '#86efac', color: '#166534', icon: '\u2705', label: 'Evidence Consistent' }
              }
              const v = verdictStyles[review.verdict] || verdictStyles.amber
              return (
                <div className="card" style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <h3 style={{ margin: 0, fontSize: 14 }}>AI Evidence Review</h3>
                    <span style={{ fontSize: 10, color: 'var(--muted)' }}>
                      {review.photos_analysed} file(s) analysed
                      {selectedCase.evidence_review_at && ` \u2022 ${formatDateTime(selectedCase.evidence_review_at)}`}
                    </span>
                  </div>
                  <div style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 12, background: v.bg, border: `1px solid ${v.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>{v.icon}</span>
                    <span style={{ fontWeight: 700, fontSize: 13, color: v.color }}>{v.label}</span>
                  </div>
                  {review.product_identified && (
                    <div style={{ padding: '6px 10px', marginBottom: 8, borderRadius: 4, background: '#f3f4f6', fontSize: 12 }}>
                      <strong style={{ color: '#374151' }}>AI identified: </strong>
                      <span style={{ color: '#4b5563' }}>{review.product_identified}</span>
                    </div>
                  )}
                  {review.red_flags?.length > 0 && review.red_flags.map((f, i) => (
                    <div key={`r${i}`} style={{ padding: '6px 10px', marginBottom: 4, borderRadius: 4, background: '#fef2f2', borderLeft: '3px solid #ef4444', fontSize: 12 }}>
                      <strong style={{ color: '#991b1b' }}>{f.title}</strong>
                      <div style={{ color: '#7f1d1d', marginTop: 2 }}>{f.detail}</div>
                    </div>
                  ))}
                  {review.amber_flags?.length > 0 && review.amber_flags.map((f, i) => (
                    <div key={`a${i}`} style={{ padding: '6px 10px', marginBottom: 4, borderRadius: 4, background: '#fffbeb', borderLeft: '3px solid #f59e0b', fontSize: 12 }}>
                      <strong style={{ color: '#92400e' }}>{f.title}</strong>
                      <div style={{ color: '#78350f', marginTop: 2 }}>{f.detail}</div>
                    </div>
                  ))}
                  {review.green_items?.length > 0 && review.green_items.map((f, i) => (
                    <div key={`g${i}`} style={{ padding: '6px 10px', marginBottom: 4, borderRadius: 4, background: '#f0fdf4', borderLeft: '3px solid #22c55e', fontSize: 12 }}>
                      <strong style={{ color: '#166534' }}>{f.title}</strong>
                      <div style={{ color: '#14532d', marginTop: 2 }}>{f.detail}</div>
                    </div>
                  ))}
                  {review.summary && (
                    <div style={{ fontSize: 12, color: '#374151', padding: '8px 10px', background: '#f9fafb', borderRadius: 4, lineHeight: 1.5, marginTop: 8 }}>
                      {review.summary}
                    </div>
                  )}
                  {review.verdict === 'red' && !['rejected', 'closed', 'return_approved'].includes(selectedCase.status) && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          className="btn btn-sm"
                          style={{ background: '#dc2626', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12 }}
                          onClick={rejectReturn}
                          disabled={rejectingReturn || queryingEvidence}
                        >
                          {rejectingReturn ? 'Rejecting...' : 'Reject Return'}
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{ background: '#f59e0b', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12 }}
                          onClick={openQueryDraft}
                          disabled={queryingEvidence || rejectingReturn || queryDraftOpen}
                        >
                          Query Customer
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{ background: '#7c3aed', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12 }}
                          onClick={overruleTroubleshooting}
                          disabled={overrulingTroubleshooting}
                        >
                          {overrulingTroubleshooting ? 'Overruling...' : 'Overrule — Troubleshoot'}
                        </button>
                      </div>
                      {queryDraftOpen && (
                        <div style={{ marginTop: 10, padding: 10, background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 6 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#92400e', marginBottom: 6 }}>Message to customer (edit as needed):</div>
                          <textarea
                            value={queryDraftMessage}
                            onChange={e => setQueryDraftMessage(e.target.value)}
                            style={{ width: '100%', minHeight: 120, padding: 8, fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
                          />
                          <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4, marginBottom: 8 }}>
                            A portal link will be appended automatically. Customer will be asked to upload additional evidence or respond via the portal.
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              className="btn btn-sm"
                              style={{ background: '#f59e0b', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12 }}
                              onClick={queryEvidence}
                              disabled={queryingEvidence || !queryDraftMessage.trim()}
                            >
                              {queryingEvidence ? 'Sending...' : 'Send Query'}
                            </button>
                            <button
                              className="btn btn-sm"
                              style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', fontWeight: 500, fontSize: 12 }}
                              onClick={() => { setQueryDraftOpen(false); setQueryDraftMessage('') }}
                              disabled={queryingEvidence}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Troubleshooting content / proposed message */}
            {(proposedMessage || selectedCase.troubleshooting_content) && (
              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 14, marginBottom: 8 }}>Troubleshooting</h3>
                {proposedMessage && selectedCase.status !== 'troubleshooting_sent' ? (
                  <>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Review the AI-generated troubleshooting before sending:</div>
                    <textarea
                      className="form-input"
                      value={proposedMessage}
                      onChange={e => setProposedMessage(e.target.value)}
                      rows={12}
                      style={{ fontSize: 12, width: '100%', marginBottom: 8, lineHeight: 1.6 }}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn btn-sm"
                        style={{ background: '#7c3aed', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12 }}
                        onClick={sendTroubleshooting}
                        disabled={sendingEmail}
                      >
                        {sendingEmail ? 'Sending...' : 'Send to Customer'}
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        style={{ fontSize: 12 }}
                        onClick={() => setProposedMessage(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : troubleshootingSteps.length > 0 ? (
                  <div style={{ fontSize: 13 }}>
                    {troubleshootingSteps.map(step => (
                      <div key={step.id} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 8,
                        padding: '8px 0', borderBottom: '1px solid #f3f4f6'
                      }}>
                        <span style={{
                          width: 20, height: 20, borderRadius: 4, flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: step.completed ? '#d1fae5' : '#f3f4f6',
                          color: step.completed ? '#059669' : '#9ca3af',
                          fontSize: 12, fontWeight: 700, marginTop: 1
                        }}>
                          {step.completed ? '\u2713' : step.step_number}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            color: step.completed ? '#059669' : '#374151',
                            textDecoration: step.completed ? 'line-through' : 'none'
                          }}>
                            {step.description}
                          </div>
                          {step.completed_at && (
                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                              Completed {formatDateTime(step.completed_at)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <div style={{
                      marginTop: 12, padding: 10, borderRadius: 6, textAlign: 'center', fontSize: 12, fontWeight: 600,
                      background: troubleshootingSteps.every(s => s.completed) ? '#d1fae5' : '#fef3c7',
                      color: troubleshootingSteps.every(s => s.completed) ? '#065f46' : '#92400e'
                    }}>
                      {troubleshootingSteps.filter(s => s.completed).length} / {troubleshootingSteps.length} steps completed
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, padding: 12, background: '#f5f3ff', borderRadius: 6, whiteSpace: 'pre-wrap', lineHeight: 1.6, maxHeight: 300, overflowY: 'auto' }}>
                    {selectedCase.troubleshooting_content}
                  </div>
                )}
              </div>
            )}

            {/* Activity Log */}
            <div className="card">
              <h3 style={{ fontSize: 14, marginBottom: 8 }}>Activity Log</h3>
              {caseHistory.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[...caseHistory].reverse().map(entry => {
                    const isStatus = entry.field === 'status'
                    const isComment = entry.field === 'comment'
                    const isRefund = entry.field === 'refund_amount'

                    let color = '#6b7280' // grey default
                    let icon = '\u2022'
                    let label = ''

                    if (isStatus) {
                      const statusLabel = STATUS_STYLES[entry.new_value]?.label || entry.new_value
                      if (entry.old_value) {
                        const oldLabel = STATUS_STYLES[entry.old_value]?.label || entry.old_value
                        label = `${oldLabel} \u2192 ${statusLabel}`
                      } else {
                        label = `Case opened as ${statusLabel}`
                      }
                      if (entry.new_value === 'rejected') { color = '#dc2626'; icon = '\u2718' }
                      else if (entry.new_value === 'closed') { color = '#6b7280'; icon = '\u2713' }
                      else if (entry.new_value === 'return_approved') { color = '#059669'; icon = '\u2713' }
                      else if (entry.new_value === 'awaiting_evidence') { color = '#d97706'; icon = '\u25B6' }
                      else if (entry.new_value === 'troubleshooting_sent') { color = '#4f46e5'; icon = '\u25B6' }
                      else { color = '#2563eb'; icon = '\u25CB' }
                    } else if (isComment) {
                      label = entry.new_value
                      if (label.startsWith('Refund of')) { color = '#7c3aed'; icon = '\u00A3' }
                      else if (label.startsWith('Case rejected')) { color = '#dc2626'; icon = '\u2718' }
                      else { color = '#6b7280'; icon = '\u2139' }
                    } else if (isRefund) {
                      label = `Refund amount set to ${entry.new_value}`
                      color = '#7c3aed'; icon = '\u00A3'
                    } else {
                      label = `${entry.field} updated`
                    }

                    const age = entry.created_at ? (() => {
                      const diff = Date.now() - new Date(entry.created_at + 'Z').getTime()
                      const mins = Math.floor(diff / 60000)
                      if (mins < 1) return 'just now'
                      if (mins < 60) return `${mins}m ago`
                      const hrs = Math.floor(mins / 60)
                      if (hrs < 24) return `${hrs}h ago`
                      const days = Math.floor(hrs / 24)
                      return `${days}d ago`
                    })() : ''

                    return (
                      <div key={entry.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, lineHeight: 1.5 }}>
                        <span style={{ color, fontWeight: 700, fontSize: 14, width: 16, textAlign: 'center', flexShrink: 0, marginTop: 1 }}>{icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: '#1f2937' }}>{label}</div>
                          <div style={{ color: '#9ca3af', fontSize: 11 }}>
                            {entry.changed_by || 'system'} &middot; {age} &middot; {formatDateTime(entry.created_at)}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div style={{ fontSize: 12, lineHeight: 2 }}>
                  <div>Case opened: {formatDateTime(selectedCase.created_at)}</div>
                  {selectedCase.status === 'logged' && !selectedCase.photos_requested_at && !selectedCase.troubleshooting_sent_at && (
                    <div style={{ color: '#6366f1', fontStyle: 'italic' }}>Awaiting reviewer assessment</div>
                  )}
                  {selectedCase.photos_requested_at && <div>Evidence requested: {formatDateTime(selectedCase.photos_requested_at)}</div>}
                  {selectedCase.evidence_received_at && <div>Evidence received: {formatDateTime(selectedCase.evidence_received_at)}</div>}
                  {selectedCase.troubleshooting_sent_at && <div>Troubleshooting sent: {formatDateTime(selectedCase.troubleshooting_sent_at)}</div>}
                  {selectedCase.approved_at && <div>Return approved: {formatDateTime(selectedCase.approved_at)}</div>}
                  {selectedCase.refunded_at && <div>Refunded: {formatDateTime(selectedCase.refunded_at)}</div>}
                  {selectedCase.closed_at && <div>Closed: {formatDateTime(selectedCase.closed_at)}</div>}
                </div>
              )}
            </div>

            {/* Internal Notes */}
            <div className="card">
              <h3 style={{ fontSize: 14, marginBottom: 8 }}>Internal Notes</h3>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <textarea
                  className="form-input"
                  placeholder="Add a note..."
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  rows={2}
                  style={{ flex: 1, fontSize: 12, resize: 'vertical' }}
                  onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) addNote() }}
                />
                <button
                  className="btn btn-sm"
                  style={{ background: '#2563eb', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12, alignSelf: 'flex-end', padding: '6px 14px' }}
                  onClick={addNote}
                  disabled={addingNote || !newNote.trim()}
                >
                  {addingNote ? 'Adding...' : 'Add'}
                </button>
              </div>
              {caseNotes.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {caseNotes.map(note => (
                    <div key={note.id} style={{ padding: '8px 10px', background: '#fefce8', borderRadius: 6, border: '1px solid #fde68a', fontSize: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, color: '#92400e' }}>{note.author_name || 'Staff'}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: '#a16207', fontSize: 11 }}>{formatDateTime(note.created_at)}</span>
                          <button
                            onClick={() => deleteNote(note.id)}
                            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '0 2px' }}
                            title="Delete note"
                          >
                            &times;
                          </button>
                        </div>
                      </div>
                      <div style={{ color: '#78350f', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{note.content}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: 8 }}>No notes yet</div>
              )}
            </div>
          </div>

          {/* Right column - Evidence */}
          <div>
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 14 }}>Evidence ({caseEvidence.length})</h3>
              </div>

              {/* Upload dropzone */}
              <div style={{
                border: '2px dashed var(--color-border, #e5e7eb)',
                borderRadius: 6,
                padding: 20,
                textAlign: 'center',
                cursor: 'pointer',
                position: 'relative',
                marginBottom: 12,
                background: uploading ? '#f0f9ff' : 'transparent'
              }}>
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handleEvidenceUpload}
                  style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                  disabled={uploading}
                />
                <div style={{ fontSize: 20, marginBottom: 4 }}>&#128247;</div>
                <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>
                  {uploading ? 'Uploading...' : 'Upload Evidence'}
                </p>
                <p style={{ fontSize: 11, color: 'var(--muted)' }}>Photos or videos</p>
              </div>

              {/* Photos */}
              {photos.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase' }}>Photos ({photos.length})</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {photos.map(p => (
                      <div key={p.id} style={{ position: 'relative', borderRadius: 6, overflow: 'hidden', border: '1px solid #e5e7eb', width: '40%' }}>
                        <img
                          src={p.public_url}
                          alt={p.original_filename}
                          style={{ width: '100%', display: 'block', cursor: 'pointer' }}
                          onClick={() => window.open(p.public_url, '_blank')}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 6px', background: '#f9fafb', fontSize: 10, color: 'var(--muted)' }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.original_filename}</span>
                          <button
                            onClick={() => deleteEvidence(p.id)}
                            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '0 4px', flexShrink: 0 }}
                          >
                            &times;
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Videos list */}
              {videos.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase' }}>Videos ({videos.length})</div>
                  {videos.map(v => (
                    <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 8, background: 'var(--bg)', borderRadius: 4, marginBottom: 4, fontSize: 12 }}>
                      <a href={v.public_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>
                        {v.original_filename}
                      </a>
                      <button
                        onClick={() => deleteEvidence(v.id)}
                        style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {caseEvidence.length === 0 && !uploading && (
                <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: 8 }}>No evidence uploaded yet</div>
              )}
            </div>
          </div>
        </div>

        {/* Partial Refund Modal */}
        {showPartialRefundModal && partialRefundData && (() => {
          const amt = parseFloat(partialRefundAmount) || 0
          const pct = partialRefundData.order_total > 0 ? ((amt / partialRefundData.order_total) * 100).toFixed(1) : '0.0'
          const exceedsMax = amt > partialRefundData.remaining_refundable
          const highRefund = parseFloat(pct) > 80
          return (
            <div className="modal-overlay" onClick={() => setShowPartialRefundModal(false)}>
              <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
                <div className="modal-header">
                  <h2>Resolve with Partial Refund</h2>
                  <button className="modal-close" onClick={() => setShowPartialRefundModal(false)}>&times;</button>
                </div>
                <div className="modal-body-scroll" style={{ padding: 20 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '10px 16px', marginBottom: 20 }}>
                    <span style={{ fontSize: 13, color: '#6b7280' }}>Order Total</span>
                    <span style={{ fontSize: 13, fontWeight: 600, textAlign: 'right' }}>&pound;{partialRefundData.order_total.toFixed(2)}</span>

                    {partialRefundData.item_cost !== null && (
                      <>
                        <span style={{ fontSize: 13, color: '#6b7280' }}>Item Cost (BP)</span>
                        <span style={{ fontSize: 13, fontWeight: 600, textAlign: 'right' }}>&pound;{partialRefundData.item_cost.toFixed(2)}</span>
                      </>
                    )}

                    {partialRefundData.already_refunded > 0 && (
                      <>
                        <span style={{ fontSize: 13, color: '#6b7280' }}>Already Refunded</span>
                        <span style={{ fontSize: 13, fontWeight: 600, textAlign: 'right', color: '#dc2626' }}>&pound;{partialRefundData.already_refunded.toFixed(2)}</span>
                      </>
                    )}

                    <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>Remaining Refundable</span>
                    <span style={{ fontSize: 13, fontWeight: 700, textAlign: 'right' }}>&pound;{partialRefundData.remaining_refundable.toFixed(2)}</span>
                  </div>

                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Settlement Amount</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: '#6b7280', fontWeight: 600 }}>&pound;</span>
                      <input
                        type="number"
                        className="form-input"
                        value={partialRefundAmount}
                        onChange={e => setPartialRefundAmount(e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="0.01"
                        max={partialRefundData.remaining_refundable}
                        style={{ width: '100%', paddingLeft: 28, fontSize: 16, fontWeight: 600 }}
                        autoFocus
                      />
                    </div>

                    {amt > 0 && (
                      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>Refund %</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: highRefund ? '#dc2626' : '#059669' }}>{pct}%</span>
                      </div>
                    )}

                    {exceedsMax && (
                      <div style={{ marginTop: 10, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 12, color: '#991b1b' }}>
                        Amount exceeds refundable balance of &pound;{partialRefundData.remaining_refundable.toFixed(2)}
                      </div>
                    )}

                    {highRefund && !exceedsMax && amt > 0 && (
                      <div style={{ marginTop: 10, padding: '8px 12px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
                        Warning: Refund is over 80% of the order total
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowPartialRefundModal(false)} disabled={partialRefundLoading}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={submitPartialRefund}
                    disabled={partialRefundLoading || !amt || amt <= 0 || exceedsMax}
                    style={{ background: '#f59e0b', borderColor: '#f59e0b' }}
                  >
                    {partialRefundLoading ? 'Processing...' : 'Confirm & Refund'}
                  </button>
                </div>
              </div>
            </div>
          )
        })()}
      </div>
    )
  }

  // Cases list view
  return (
    <div>
      {/* Status filter pills */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {STATUS_FILTERS.map(f => (
          <button
            key={f.value}
            className="btn btn-sm"
            style={{
              background: statusFilter === f.value ? '#2563eb' : '#f3f4f6',
              color: statusFilter === f.value ? '#fff' : '#374151',
              border: 'none',
              fontWeight: 600,
              fontSize: 12,
              padding: '6px 14px',
              borderRadius: 20
            }}
            onClick={() => setStatusFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : cases.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Cases Found</div>
            <div className="empty-state-description">No cases match the selected filter.</div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Case</th>
                <th>Product</th>
                <th className="col-mobile-hide">Order</th>
                <th className="col-mobile-hide">Customer</th>
                <th>Reason</th>
                <th>Status</th>
                <th className="col-mobile-hide">Opened</th>
              </tr>
            </thead>
            <tbody>
              {cases.map(c => (
                <tr
                  key={c.id}
                  onClick={() => selectCase(c)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ fontWeight: 600 }}>
                    {c.public_id || '#' + c.id}
                    {c.channel && c.channel !== 'shopify' && (
                      <span style={{
                        marginLeft: 6, padding: '1px 4px', borderRadius: 3, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', verticalAlign: 'middle',
                        background: c.channel === 'ebay' ? '#e4f0fb' : '#f3e8ff',
                        color: c.channel === 'ebay' ? '#0654ba' : '#6b21a8'
                      }}>
                        {c.channel}
                      </span>
                    )}
                  </td>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.product_title || '-'}
                  </td>
                  <td className="col-mobile-hide">{c.order_name || '-'}</td>
                  <td className="col-mobile-hide">{c.customer_name || c.customer_email || '-'}</td>
                  <td>{c.reason_name}</td>
                  <td><StatusBadge status={c.status} evidenceReceived={!!c.evidence_received_at} /></td>
                  <td className="col-mobile-hide">{formatDate(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default CasesTab
